import React, { useMemo } from "react"

import { EqualHeightElement } from "react-equal-height/clean"
import { Product } from "../../../shared/sdk/types"
import { Text } from "react-bricks/frontend"
import { formatPrice } from "../../../shared/utils/format"
import i18n from "../../../i18n/config"
import { useLocalesState } from "../../../shared/context/locales"

export interface MatrixItemPriceProps {
    row: number
    product?: Product
    realPrice?: boolean
}

const getPrice = (product: Product, language: string, realPrice?: boolean) => {
    return realPrice
        ? formatPrice(product.price.current.taxed, product.price.currency.name, language)
        : product.sku === "LV2START"
        ? formatPrice(product.price.current.taxed / (product.configuration.splitted?.parts || 1), product.price.currency.name, language)
        : product.sku === "LVSTARTNA"
        ? formatPrice(product.price.current.taxed / 3, product.price.currency.name, language)
        : product.configuration.subscription
        ? formatPrice(
              (product.price.current.taxed * (product.configuration.subscription.max_quantity - 1)) /
                  product.configuration.subscription.max_quantity,
              product.price.currency.name,
              language
          ) + "*"
        : formatPrice(product.price.current.taxed, product.price.currency.name, language)
}

const getPriceAddition = (product: Product, language: string, realPrice?: boolean) => {
    return (product.sku === "LV2START" || product.sku === "LVSTARTNA") && !realPrice
        ? i18n.t("price_total") +
        " " +
        formatPrice(product.price.current.taxed, product.price.currency.name, language)
        : undefined
}

const getSmallprint = (product: Product, language: string) => {
    return !["Liter", "litre"].includes(product.price.basic.unit)
        ? i18n.t("price_incl_vat")
        : product.configuration.subscription
        ? i18n.t("price_per_litre_subscription_asterisk", {
              basic_price: formatPrice(product.price.basic.taxed, product.price.currency.name, language),
              amount: product.configuration.subscription.max_quantity,
              price: formatPrice(product.price.current.taxed, product.price.currency.name, language),
              discount_price: formatPrice(
                  (product.price.current.taxed * (product.configuration.subscription.max_quantity - 1)) /
                      product.configuration.subscription.max_quantity,
                  product.price.currency.name,
                  language
              ),
          })
        : i18n.t("price_per_litre", {
              price: formatPrice(product.price.basic.taxed, product.price.currency.name, language),
          })
}

const MatrixItemPrice = ({ row, product, realPrice }: MatrixItemPriceProps) => {
    const { language } = useLocalesState()

    if (!product) return null

    const price = useMemo(() => getPrice(product, language, realPrice), [product, language, realPrice])
    const priceAddition = useMemo(() => getPriceAddition(product, language, realPrice), [product, language, realPrice])
    const smallprint = useMemo(() => getSmallprint(product, language), [product, language])

    return (
        <div className="pb-7 pt-3">
            <EqualHeightElement name={"price-" + row}>
                <Text
                    propName="label"
                    renderBlock={({ children }) => <p className="mb-1 text-sm text-dark-grey">{children}</p>}
                    placeholder="Lorem ipsum"
                />
                <div className="mb-3.5 flex items-end gap-3 sm:mb-6">
                    <p className="text-base font-semibold md:text-md [&+*]:grow">{price}</p>
                    {priceAddition ? (
                        <p className="text-sm">{priceAddition}</p>
                    ) : (
                        <Text
                            propName="priceAddition"
                            renderBlock={({ children }) => <p className="text-sm">{children}</p>}
                            placeholder="Lorem ipsum"
                        />
                    )}
                </div>
                <small className="text-box whitespace-pre-wrap text-xs text-dark-grey">{smallprint}</small>
            </EqualHeightElement>
        </div>
    )
}

export default MatrixItemPrice
